import { useState } from "react";
import Collapse from "../../../components/Collapse";
import { formatBook } from "../../../utils/formatOdds";

const JOKER_IMG = `${import.meta.env.BASE_URL}img/joker1`;

// Reference v-kvalpoint: >= 1000 → "<n/1000 to 2dp>K", otherwise the plain number (300000 → "300K").
function kvalPoint(num) {
  const n = Number(num);
  if (!Number.isFinite(n)) return "0";
  if (n >= 1000) return `${parseFloat((n / 1000).toFixed(2))}K`;
  return String(parseFloat(n));
}

// Reference getJokerCard: sid 101 (Player A of joker 10) uses image 10.
const jokerCard = (sid) => (String(sid) === "101" ? "10" : String(sid));

/**
 * Unlimited Joker Oneday / 20-20 (joker1, joker120) bet table — reference markup:
 *   .teen1daycasino-container.mt-2 > .teen1dayleft > one .casino-box-row per feed `sub` entry (sorted by sr):
 *     .casino-nation-name (joker image for Player A rows, b nation, float-right book + range toggle)
 *     .casino-bl-box (back + lay, .suspended when gstatus is not OPEN).
 * Range ids are `range<sid>`. `children` (remark, Last Result title + strip) render inside the container
 * after the left column, as in the reference.
 */
const BetTableTeenJoker = ({ data = [], onBetClick, exposures = {}, children }) => {
  // Reference = independent Bootstrap collapses: undefined = never toggled, then true (open) / false (closed).
  const [ranges, setRanges] = useState({});
  const toggleRange = (rangeId) => setRanges((prev) => ({ ...prev, [rangeId]: !prev[rangeId] }));

  const book = (item) => {
    const nat = item?.nat || "";
    const raw =
      exposures[`${nat}${item?.sid ?? ""}`] ??
      exposures[String(item?.sid ?? "")] ??
      exposures[nat] ??
      exposures[nat.toLowerCase()] ??
      0;
    const exp = Number(raw) || 0;
    return {
      cls: exp > 0 ? "book-green" : "book-red",
      text: formatBook(exp),
    };
  };

  const renderRow = (item) => {
    const rangeId = `range${item.sid}`;
    const isOpen = !!ranges[rangeId];
    const locked = String(item?.gstatus || "").toUpperCase() !== "OPEN";
    const bk = book(item);
    const onToggle = (e) => {
      e.stopPropagation();
      toggleRange(rangeId);
    };

    return (
      <div className="casino-box-row" key={`${item.sid}-${item.sr}`}>
        <div className="casino-nation-name">
          {item.nat === "Player A" ? <img src={`${JOKER_IMG}/${jokerCard(item.sid)}.png`} alt="" /> : null}{" "}
          <b>{item.nat}</b>{" "}
          <div className="float-right">
            <span className={`mr-2 ${bk.cls}`}>{bk.text}</span>{" "}
            <i
              role="button"
              tabIndex={0}
              onClick={onToggle}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), toggleRange(rangeId))}
              data-toggle="collapse"
              data-target={`#${rangeId}`}
              aria-expanded={isOpen ? "true" : "false"}
              className={`fas fa-info-circle${isOpen ? "" : " collapsed"}`}
            />{" "}
            <Collapse id={rangeId} open={!!isOpen} className="icon-range">
              {"\n                        R:"}
              <span>{kvalPoint(item.min)}</span>-<span>{kvalPoint(item.max)}</span>
            </Collapse>
          </div>
        </div>{" "}
        <div className="casino-bl-box">
          <div
            className={`back casino-bl-box-item${locked ? " suspended" : ""}`}
            onClick={() => !locked && item.b && onBetClick(item.b, item.nat, item, "back")}
          >
            <span className="casino-box-odd">{item.b ?? 0}</span>
          </div>{" "}
          <div
            className={`lay casino-bl-box-item${locked ? " suspended" : ""}`}
            onClick={() => !locked && item.l && onBetClick(item.l, item.nat, item, "lay")}
          >
            <span className="casino-box-odd">{item.l ?? 0}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="teen1daycasino-container mt-2">
      <div className="teen1dayleft">{data.map(renderRow)}</div> {children}
    </div>
  );
};

export default BetTableTeenJoker;
